import React, { useEffect, useRef, useState } from "react";
import {
  DescribeSignalingChannelCommand,
  DescribeSignalingChannelCommandOutput,
  GetSignalingChannelEndpointCommand,
  GetSignalingChannelEndpointOutput,
  KinesisVideo,
  ResourceEndpointListItem,
  Status,
} from "@aws-sdk/client-kinesis-video";
import { Role, SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import { v4 as uuid } from "uuid";

import {
  ERROR_ICE_CANDIDATE_NOT_FOUND,
  ERROR_PEER_CONNECTION_LOCAL_DESCRIPTION_REQUIRED,
  ERROR_PEER_CONNECTION_NOT_INITIALIZED,
  ERROR_SIGNALING_CLIENT_NOT_CONNECTED,
  ERROR_CHANNEL_ARN_MISSING,
  ERROR_RESOURCE_ENDPOINT_LIST_MISSING,
  ERROR_SIGNALING_CLIENT,
  ERROR_CHANNEL_ARN,
  ERROR_RESOURCE_ENDPOINT,
  ERROR_SDP_OFFER,
  ERROR_SDP_ANSWER,
} from "../constants";
import { PeerConfigOptions, Peer } from "../model";
import { getLogger } from "../../../../Models/commons";

type SignalingChannelEndpoints = {
  WSS?: string;
  HTTPS?: string;
};

export function useViewerVicon(config: any) {
  const {
    channelName,
    credentials,
    region,
    clientName,
    trickleICE = true,
    offerToReceiveAudio,
    offerToSendAudio,
    localMediaStream,
    requestAuthorizationToTalk,
    messageToDevice,
  } = config;

  const kinesisVideoClientRef = useRef<KinesisVideo>(
    new KinesisVideo({
      region,
      credentials,
    })
  );
  const [channelARN, setChannelARN] = useState<string>("");
  const [signalingChannelEndpoints, setSignalingChannelEndpoints] =
    useState<SignalingChannelEndpoints>();
  const kinesisVideoClient = kinesisVideoClientRef.current;
  const [iceServers, setIceServers] = useState<RTCIceServer[]>([]);
  const [signalingClient, setSignalingClient] = useState<SignalingClient>();
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection>();
  const clientId = useRef<string>(clientName + "2a88mo88a2" + uuid());
  const [peerMedia, setPeerMedia] = useState<MediaStream>();
  const [peerError, setPeerError] = useState<Error>();
  const [dataChannel, setDataChannel] = useState<RTCDataChannel>();
  const [dataChannelMessage, setDataChannelMessage] = useState("");
  const [isDataChannelOpen, setIsDataChannelOpen] = useState(false);
  const [connectionStateStatus, setConnectionStateStatus] = useState("");
  const [triggerHandshake, setTriggerHandshake] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const logger = useRef(getLogger());

  const closeConnection = () => {
    logger.current.logViewer(`[${clientId.current}] closeconnection`);

    if (signalingClient) {
      signalingClient.off("open", () => {
        //
      });
      signalingClient.off("sdpAnswer", () => {
        //
      });
      signalingClient.off("iceCandidate", () => {
        //
      });

      signalingClient.removeAllListeners();
      signalingClient.close();
    }

    if (peerConnection) {
      peerConnection.removeEventListener("icecandidate", () => {
        //
      });
      peerConnection.removeEventListener("track", () => {
        //
      });
      peerConnection.close();
    }

    (dataChannel as any)?.close();
    setSignalingClient(undefined);
    setPeerConnection(undefined);
    setPeerMedia(undefined);
    setConnectionStateStatus("");
    setChannelARN("");
    setSignalingChannelEndpoints(undefined);
    setIceServers([]);
    setDataChannel(undefined);
    setIsDataChannelOpen(false);
  };

  const setReload = () => {
    logger.current.logViewer(`[${clientId.current}] reload`);
    closeConnection();
    setTriggerHandshake((triggerHandshake) => !triggerHandshake);
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const params = {
          ChannelName: channelName,
        };
        const describeCommand = new DescribeSignalingChannelCommand(params);
        const describeResponse = await kinesisVideoClient.send(describeCommand);
        if (!isMounted) {
          return;
        }
        if (
          describeResponse.ChannelInfo?.ChannelARN !== "" &&
          describeResponse.ChannelInfo?.ChannelStatus === Status.ACTIVE
        ) {
          setChannelARN(describeResponse.ChannelInfo?.ChannelARN as string);
          return;
        }
        setErrorMessage(ERROR_CHANNEL_ARN);
      } catch {
        setErrorMessage(ERROR_CHANNEL_ARN);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [triggerHandshake]);

  useEffect(() => {
    if (!channelARN.length) {
      return;
    }
    let isMounted = true;
    const load = async () => {
      try {
        const getSignalCommand = new GetSignalingChannelEndpointCommand({
          ChannelARN: channelARN,
          SingleMasterChannelEndpointConfiguration: {
            Protocols: ["WSS", "HTTPS"],
            Role: Role.VIEWER,
          },
        });
        const getSignalResponse = await kinesisVideoClient.send(getSignalCommand);
        if (!isMounted) {
          return;
        }
        if (!Array.isArray(getSignalResponse?.ResourceEndpointList)) {
          throw new Error(ERROR_RESOURCE_ENDPOINT);
        }
        const endpointsByProtocol = getSignalResponse.ResourceEndpointList.reduce(
          (endpoints: SignalingChannelEndpoints, endpoint: ResourceEndpointListItem) => {
            if (!endpoint.Protocol) {
              return endpoints;
            }
            endpoints[endpoint.Protocol as "WSS" | "HTTPS"] = endpoint.ResourceEndpoint;
            return endpoints;
          },
          <SignalingChannelEndpoints>{}
        );
        setSignalingChannelEndpoints(endpointsByProtocol);
      } catch {
        setErrorMessage(ERROR_RESOURCE_ENDPOINT);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [channelARN]);

  useEffect(() => {
    if (!signalingChannelEndpoints) {
      return;
    }

    setIceServers([
      { urls: process.env.REACT_APP_STUN as string },
      { urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` },
      {
        urls: [
          process.env.REACT_APP_TURN1 as string,
          process.env.REACT_APP_TURN2 as string,
          process.env.REACT_APP_TURN3 as string,
          process.env.REACT_APP_TURN4 as string,
          process.env.REACT_APP_TURN5 as string,
          process.env.REACT_APP_TURN6 as string,
        ],
        username: process.env.REACT_APP_TURN_User,
        credential: process.env.REACT_APP_TURN_Password,
      },
    ]);

    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    setSignalingClient(
      new SignalingClient({
        channelARN,
        channelEndpoint: signalingChannelEndpoints.WSS as string,
        clientId: clientId.current,
        credentials: { accessKeyId, secretAccessKey, sessionToken },
        region,
        role: Role.VIEWER,
        systemClockOffset: kinesisVideoClient.config.systemClockOffset,
      })
    );
  }, [signalingChannelEndpoints]);

  useEffect(() => {
    if (!iceServers.length) {
      return;
    }

    setPeerConnection(
      new RTCPeerConnection({
        iceServers,
        iceTransportPolicy: "all",
      })
    );
  }, [iceServers]);

  useEffect(() => {
    if (!signalingClient || !peerConnection) {
      return;
    }

    signalingClient.on("open", async () => {
      logger.current.logViewer(`[${clientId.current}] signaling client opened`);

      //   if (viewerOnly) {
      //     peerConnection?.addTransceiver("video");
      //     peerConnection?.getTransceivers().forEach((t) => (t.direction = "recvonly"));
      //   }

      const sessionDescription = await peerConnection.createOffer({
        offerToReceiveAudio: offerToReceiveAudio,
        offerToReceiveVideo: true,
      });

      try {
        await peerConnection.setLocalDescription(sessionDescription);
        if (!peerConnection.localDescription) {
          throw new Error(ERROR_SDP_OFFER);
        }
        if (trickleICE) {
          logger.current.logViewer(
            `[${clientId.current}] sending sdp offer---[${JSON.stringify(
              peerConnection.localDescription
            )}]`
          );
          signalingClient.sendSdpOffer(peerConnection.localDescription);
        }
      } catch (error) {
        logger.current.logError(error);
        setErrorMessage(ERROR_SDP_OFFER);
      }
    });

    signalingClient.on("sdpAnswer", async (answer: RTCSessionDescriptionInit) => {
      logger.current.logViewer(
        `[${clientId.current}] received sdp answer---[${JSON.stringify(answer)}]`
      );
      try {
        await peerConnection.setRemoteDescription(answer);
      } catch (error) {
        logger.current.logError(error);
        setErrorMessage(ERROR_SDP_ANSWER);
      }
    });

    signalingClient.on("iceCandidate", ({ candidate }: RTCPeerConnectionIceEvent) => {
      logger.current.logViewer(`[${clientId.current}] received peer ice candidate`);

      try {
        if (!signalingClient) {
          throw new Error(ERROR_SIGNALING_CLIENT_NOT_CONNECTED);
        }

        if (candidate) {
          if (trickleICE) {
            signalingClient.sendIceCandidate(candidate);
          } else {
            if (!trickleICE) {
              const sdpOffer: RTCSessionDescription =
                peerConnection.localDescription as RTCSessionDescription;
              signalingClient?.sendSdpOffer(sdpOffer);
            }
          }
        }
      } catch (error) {
        logger.current.logError(error);
        return setPeerError(error as Error);
      }
    });

    signalingClient.on("error", (error: Error) => {
      logger.current.logError(error);
      return setErrorMessage(ERROR_SIGNALING_CLIENT);
    });

    peerConnection.addEventListener("icecandidate", ({ candidate }: RTCPeerConnectionIceEvent) => {
      logger.current.logViewer(`[${clientId.current}] received peer ice candidate`);

      try {
        if (candidate) {
          if (trickleICE) {
            signalingClient.sendIceCandidate(candidate);
          } else {
            if (!trickleICE) {
              const sdpOffer: RTCSessionDescription =
                peerConnection.localDescription as RTCSessionDescription;
              signalingClient.sendSdpOffer(sdpOffer);
            }
          }
        }
      } catch (error) {
        logger.current.logError(error);
        return setPeerError(error as Error);
      }
    });

    peerConnection.addEventListener("track", ({ streams = [] }: RTCTrackEvent) => {
      logger.current.logViewer(`[${clientId.current}] received peer track`);
      setPeerMedia(streams[0]);
    });

    peerConnection.onconnectionstatechange = (event: Event) => {
      setConnectionStateStatus(peerConnection.connectionState || "");
      switch (peerConnection.connectionState) {
        case "new":
          setPeerError(undefined);
          logger.current.logViewer(`[${clientId.current}] onconnectionstatechange NEW`);
          break;
        case "connected":
          logger.current.logViewer(`[${clientId.current}] onconnectionstatechange connected`);
          setPeerError(undefined);
          break;
        case "failed":
          logger.current.logViewer(`[${clientId.current}] onconnectionstatechange failed`);
          break;
        case "disconnected":
          logger.current.logViewer(`[${clientId.current}] onconnectionstatechange disconnected`);
          break;
        case "closed":
          logger.current.logViewer(`[${clientId.current}] onconnectionstatechange closed`);
          break;
      }
    };

    peerConnection.onsignalingstatechange = (event: Event) => {
      switch (peerConnection?.signalingState) {
        case "stable":
          logger.current.logViewer(`[${clientId.current}] onsignalingstatechange stable`);
          break;
        case "have-remote-pranswer":
          logger.current.logViewer(
            `[${clientId.current}] onsignalingstatechange have-remote-pranswer`
          );
          break;
        case "have-remote-offer":
          logger.current.logViewer(
            `[${clientId.current}] onsignalingstatechange have-remote-offer`
          );
          break;
        case "have-local-offer":
          logger.current.logViewer(`[${clientId.current}] onsignalingstatechange have-local-offer`);
          break;
        case "have-local-pranswer":
          logger.current.logViewer(
            `[${clientId.current}] onsignalingstatechange have-local-pranswer`
          );
          break;
        case "closed":
          logger.current.logViewer(`[${clientId.current}] onsignalingstatechange closed`);
          break;
      }
    };

    peerConnection.oniceconnectionstatechange = (event: Event) => {
      switch (peerConnection?.iceConnectionState) {
        case "failed":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange failed`);
          break;
        case "disconnected":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange disconnected`);
          break;
        case "closed":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange closed`);
          break;
        case "checking":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange checking`);
          break;
        case "connected":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange connected`);
          break;
        case "completed":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange completed`);
          break;
        case "new":
          logger.current.logViewer(`[${clientId.current}] oniceconnectionstatechange new`);
          break;
      }
    };

    peerConnection.onnegotiationneeded = async (event: Event) => {
      //aws doesnt have this feature
      logger.current.logViewer(`[${clientId.current}] onnegotiationneeded`);
    };

    signalingClient.open();
  }, [peerConnection, signalingClient]);

  useEffect(() => {
    if (!peerConnection) {
      return;
    }

    const temp1 = channelName.replaceAll("-", "_");
    const dataChannelTemp = peerConnection.createDataChannel("chat_" + temp1, {
      ordered: true,
      // negotiated: true,
    });

    setDataChannel(dataChannelTemp as any);

    peerConnection.ondatachannel = (event: RTCDataChannelEvent) => {
      event.channel.onmessage = (event: MessageEvent) => {
        setDataChannelMessage(event.data);
      };
    };

    dataChannelTemp.onopen = (event: Event) => {
      setIsDataChannelOpen(true);
      logger.current.logViewer(`[${clientId.current}] dataChannel opened`);
    };

    dataChannelTemp.onerror = () => {
      setIsDataChannelOpen(false);
      logger.current.logViewer(`[${clientId.current}] dataChannel failed`);
    };

    dataChannelTemp.onclose = () => {
      setIsDataChannelOpen(false);
      logger.current.logViewer(`[${clientId.current}] dataChannel closed`);
    };
  }, [peerConnection]);

  useEffect(() => {
    return function cleanup() {
      peerMedia?.getTracks().forEach((track) => track.stop());
    };
  }, [peerMedia]);

  /** Send local media stream to remote peer. */
  useEffect(() => {
    if (!localMediaStream || !peerConnection) {
      return;
    }
    if (peerConnection?.connectionState === "closed") {
      return;
    }
    if (offerToSendAudio) {
      localMediaStream.getTracks().forEach((track: MediaStreamTrack) => {
        (peerConnection as RTCPeerConnection).addTrack(track, localMediaStream);
      });
    }
  }, [peerConnection]);

  useEffect(() => {
    if (isDataChannelOpen) {
      const object = {
        status: requestAuthorizationToTalk,
      };
      logger.current.logViewer(
        `[${clientId.current}] requestAuthorizationToTalk ${JSON.stringify(object)}`
      );
      (dataChannel as any).send(JSON.stringify(object));
    }
  }, [requestAuthorizationToTalk, isDataChannelOpen]);

  useEffect(() => {
    if (isDataChannelOpen && messageToDevice) {
      logger.current.logViewer(`[${clientId.current}] requesting ${messageToDevice}`);
      (dataChannel as any).send(messageToDevice);
    }
  }, [messageToDevice, isDataChannelOpen]);

  const peer: Peer = {
    id: clientId.current,
    connection: peerConnection,
    remoteMediaStream: peerMedia,
  };

  return {
    error: peerError || errorMessage,
    // localMediaStream,
    peer,
    remoteDataMessage: dataChannelMessage,
    connectionStateStatus,
    setReload,
    closeConnection,
  };
}
